import { useState, type FormEvent } from 'react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';

export default function LoginPage() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Success state drives the "welcome" flash + card fade before the
  // dashboard takes over on the next auth state change.
  const [success, setSuccess] = useState(false);
  // Toggled by "نسيت كلمة السر" — swaps the sign-in form for a
  // single-field reset form on the same card, so nobody navigates
  // away and loses their in-progress email.
  const [forgot, setForgot] = useState(false);
  // MFA challenge state — set after a successful password sign-in
  // if the account has a verified TOTP factor. Password auth alone
  // yields an aal1 session; the challenge below upgrades it to aal2.
  const [mfa, setMfa] = useState<{ factorId: string; challengeId: string } | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);

    if (forgot) {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed) {
        setError('اكتب الإيميل الأول');
        return;
      }
      setBusy(true);

      // Admin dashboards have a small closed list of accounts. Rather
      // than the generic "if the email exists…" vague response, we tell
      // the admin directly whether they typed a registered address.
      //
      // The lookup goes through the `admin-email-check` edge function,
      // which fail-CLOSED IP-rate-limits (5/min) — the underlying
      // admin_email_exists RPC had its anon EXECUTE grant revoked
      // precisely to close the enumeration surface.
      const { data: checkData, error: checkErr } = await supabase.functions.invoke(
        'admin-email-check',
        { body: { email: trimmed } },
      );
      if (checkErr) {
        setBusy(false);
        setError('مقدرناش نتحقق من الإيميل — حاول تاني بعد شويه');
        return;
      }
      if (!checkData?.exists) {
        setBusy(false);
        setError('الإيميل ده مش مسجّل. راجع الإيميل أو اطلب من مدير النظام يضيفك.');
        return;
      }

      const redirectTo = `${window.location.origin}/set-password`;
      const { error: err } = await supabase.auth.resetPasswordForEmail(trimmed, {
        redirectTo,
      });
      setBusy(false);
      if (err) {
        setError(err.message);
        return;
      }
      setNotice('تمام — بعتنالك رابط تغيير كلمة السر على الإيميل. لو ما لقيتوش راجع الـ Spam.');
      return;
    }

    setBusy(true);
    const { error: err } = await signIn(email.trim(), password);
    if (err) {
      setBusy(false);
      setError(err);
      return;
    }

    // Check whether MFA is required. AAL (Assurance Level) 1 = password
    // only; AAL 2 = password + TOTP. If the account enrolled a factor
    // in /security, Supabase returns nextLevel = 'aal2' here.
    const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aalData?.currentLevel === 'aal1' && aalData?.nextLevel === 'aal2') {
      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      const verified = factorsData?.totp?.find((f) => f.status === 'verified');
      if (verified) {
        const { data: chal, error: chalErr } = await supabase.auth.mfa.challenge({ factorId: verified.id });
        if (chalErr || !chal) {
          setBusy(false);
          setError(chalErr?.message ?? 'مقدرناش نبدأ التحقق بخطوتين — حاول تاني');
          return;
        }
        setMfa({ factorId: verified.id, challengeId: chal.id });
        setBusy(false);
        return;
      }
    }

    // No MFA required — celebrate and let AuthProvider swap in the dashboard.
    setSuccess(true);
    setTimeout(() => setBusy(false), 900);
  };

  const verifyMfa = async () => {
    if (!mfa) return;
    const trimmed = mfaCode.replace(/\s/g, '');
    if (!/^\d{6}$/.test(trimmed)) {
      setError('اكتب الكود اللي في تطبيق المصادقة (6 أرقام)');
      return;
    }
    setError(null);
    setBusy(true);
    const { error: verErr } = await supabase.auth.mfa.verify({
      factorId: mfa.factorId,
      challengeId: mfa.challengeId,
      code: trimmed,
    });
    if (verErr) {
      setBusy(false);
      setError(verErr.message === 'Invalid TOTP code entered' ? 'الكود غلط، جرب تاني' : verErr.message);
      return;
    }
    setSuccess(true);
    setMfa(null);
    setMfaCode('');
    setTimeout(() => setBusy(false), 900);
  };

  return (
    <div className={`center login-scene ${success ? 'login-scene--success' : ''}`}>
      {/* Soft brand aurora behind everything so the card floats rather
          than sits on a flat page. */}
      <div className="login-aurora" aria-hidden="true">
        <span className="glow glow-a" />
        <span className="glow glow-b" />
      </div>

      <form className={`login-card ${success ? 'login-card--success' : ''}`} onSubmit={submit}>
        <div className="login-brand">
          <div className="logo-halo" aria-hidden="true" />
          <img
            className="logo"
            src={`${import.meta.env.BASE_URL}brand/mersal-logo.png`}
            alt="Mersal"
          />
          <img
            className="slogan"
            src={`${import.meta.env.BASE_URL}brand/slogan.png`}
            alt="ابعت فرحة"
          />
        </div>

        <div className="login-heading">
          <h1 className="login-title" data-text="وقتك ومجهودك بيفرق مع مرسال">
            وقتك ومجهودك بيفرق مع مرسال
          </h1>
          <span className="login-underline" aria-hidden="true" />
        </div>

        {error && <div className="error-inline">{error}</div>}
        {notice && !error && (
          <div
            className="error-inline"
            style={{ background: 'rgba(31,157,85,0.12)', color: 'var(--teal-dark)', border: '1px solid rgba(31,157,85,0.35)' }}
          >
            {notice}
          </div>
        )}

        <div className={`field field--interactive ${email ? 'field--filled' : ''}`}>
          <label htmlFor="email">البريد الإلكتروني</label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {!forgot && !mfa && (
          <div className={`field field--interactive ${password ? 'field--filled' : ''}`}>
            <label htmlFor="password">كلمة السر</label>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        )}

        {mfa && (
          <div className={`field field--interactive ${mfaCode ? 'field--filled' : ''}`}>
            <label htmlFor="mfa">الكود من تطبيق المصادقة (6 أرقام)</label>
            <input
              id="mfa"
              type="text"
              inputMode="numeric"
              maxLength={7}
              autoComplete="one-time-code"
              autoFocus
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter' && mfaCode.length === 6) { e.preventDefault(); void verifyMfa(); } }}
              style={{ fontSize: 22, letterSpacing: 8, textAlign: 'center', fontFamily: 'monospace' }}
            />
          </div>
        )}

        <div className="login-btn-row">
          <button
            type={mfa ? 'button' : 'submit'}
            onClick={mfa ? (e) => { e.preventDefault(); void verifyMfa(); } : undefined}
            className="btn login-btn"
            disabled={busy || (mfa !== null && mfaCode.length !== 6)}
          >
            {success ? (
              <span className="login-btn-success" aria-hidden="true">
                <span className="checkmark" />
                <span>أهلاً بيك</span>
              </span>
            ) : busy ? (
              forgot ? 'جاري الإرسال...' : mfa ? 'جاري التحقق...' : 'جاري الدخول...'
            ) : forgot ? (
              'ابعت لينك تغيير كلمة السر'
            ) : mfa ? (
              'أكّد الكود'
            ) : (
              'تسجيل الدخول'
            )}
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <button
            type="button"
            onClick={() => {
              setForgot((f) => !f);
              setError(null);
              setNotice(null);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--teal)',
              fontFamily: 'inherit',
              fontSize: 13,
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: 6,
            }}
          >
            {forgot ? 'الرجوع لتسجيل الدخول' : 'نسيت كلمة السر؟'}
          </button>
        </div>
      </form>
    </div>
  );
}
